import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import styled from "styled-components";

import { colorsPalettes, setElevation } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";

import { useIsolateOverlayEvents } from "./useIsolateOverlayEvents";

const Dialog = styled.dialog`
    border: none;
    padding: 0;
    margin: 0;
    background: transparent;
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;

    &::-webkit-scrollbar {
        width: 12px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${colorsPalettes.carbon[300]};
        border-radius: 6px;
        border: 2px solid transparent;
        background-clip: content-box;
    }
    &::-webkit-scrollbar-thumb:hover {
        background-color: ${colorsPalettes.carbon[400]};
    }

    &::backdrop {
        background-color: rgba(0, 0, 0, 0.85);
    }
`;

const Stage = styled.div<{ $isZoomed: boolean }>`
    width: 100%;
    min-height: 100vh;
    display: flex;
    align-items: ${({ $isZoomed }) => ($isZoomed ? "flex-start" : "center")};
    justify-content: center;
    padding: 10vh 10vw;
    box-sizing: border-box;
`;

const PreviewImage = styled.img<{ $isZoomed: boolean }>`
    display: block;
    user-select: none;
    -webkit-user-drag: none;
    cursor: ${({ $isZoomed }) => ($isZoomed ? "zoom-out" : "zoom-in")};
    max-width: 100%;
    max-height: ${({ $isZoomed }) => ($isZoomed ? "9999px" : "80vh")};
    width: auto;
    height: auto;
    object-fit: contain;
`;

const Toolbar = styled.div`
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 1;
    display: flex;
    gap: 8px;
`;

const ToolbarButton = styled.div`
    background-color: ${colorsPalettes.carbon[0]};
    border-radius: 50%;
    ${setElevation("Z2")}
`;

interface IImagePreviewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    src: string | null;
    alt?: string;
}

export const ImagePreviewDialog = ({ isOpen, onClose, src, alt }: IImagePreviewDialogProps) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const pendingScrollRatioRef = useRef<number | null>(null);
    const [isZoomed, setIsZoomed] = useState(true);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) {
            return;
        }
        if (isOpen && !dialog.open) {
            setIsZoomed(true);
            pendingScrollRatioRef.current = null;
            dialog.showModal();
            dialog.scrollTo({ top: 0, behavior: "instant" });
        } else if (!isOpen && dialog.open) {
            dialog.close();
        }
    }, [isOpen]);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) {
            return;
        }
        const handleNativeClose = () => onClose();
        dialog.addEventListener("close", handleNativeClose);
        return () => dialog.removeEventListener("close", handleNativeClose);
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const handlePinch = (event: WheelEvent) => {
            if (event.ctrlKey) {
                event.preventDefault();
            }
        };
        window.addEventListener("wheel", handlePinch, { passive: false });
        return () => window.removeEventListener("wheel", handlePinch);
    }, [isOpen]);

    useIsolateOverlayEvents({ isActive: isOpen, targetRef: dialogRef });

    const handleToggleZoom = useCallback((event: React.MouseEvent<HTMLImageElement>) => {
        setIsZoomed((prev) => {
            const next = !prev;
            if (next && imageRef.current) {
                const rect = imageRef.current.getBoundingClientRect();
                pendingScrollRatioRef.current = rect.height > 0 ? (event.clientY - rect.top) / rect.height : null;
            } else {
                pendingScrollRatioRef.current = null;
            }
            return next;
        });
    }, []);

    useLayoutEffect(() => {
        const ratio = pendingScrollRatioRef.current;
        const dialog = dialogRef.current;
        const image = imageRef.current;
        if (!isZoomed || ratio === null || !dialog || !image) {
            return;
        }
        pendingScrollRatioRef.current = null;
        const targetY = image.offsetTop + ratio * image.offsetHeight - dialog.clientHeight / 2;
        dialog.scrollTo({ top: Math.max(0, targetY), behavior: "instant" });
    }, [isZoomed]);

    const handleDownload = useCallback(() => {
        if (!src) {
            return;
        }
        const link = document.createElement("a");
        link.href = src;
        link.download = "";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
    }, [src]);

    const handleStageClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (event.target === event.currentTarget) {
                onClose();
            }
        },
        [onClose],
    );

    return (
        <Dialog
            ref={dialogRef}
            data-automation="image-preview-dialog"
        >
            <Toolbar>
                <ToolbarButton>
                    <IconButton
                        type="flat"
                        iconName="download"
                        iconSize="sm"
                        onClick={handleDownload}
                        data-automation="image-preview-download"
                    />
                </ToolbarButton>
                <ToolbarButton>
                    <IconButton
                        type="flat"
                        iconName="clear"
                        iconSize="sm"
                        onClick={onClose}
                        data-automation="image-preview-close"
                    />
                </ToolbarButton>
            </Toolbar>
            <Stage
                $isZoomed={isZoomed}
                onClick={handleStageClick}
            >
                {src ? (
                    <PreviewImage
                        ref={imageRef}
                        src={src}
                        alt={alt ?? ""}
                        $isZoomed={isZoomed}
                        onClick={handleToggleZoom}
                        draggable={false}
                    />
                ) : null}
            </Stage>
        </Dialog>
    );
};
